import { NativeStackScreenProps } from "@react-navigation/native-stack";
import React from "react";
import { View } from "react-native";
import { Phrase, PhraseKey } from "../common/phrases";
import FieldsRow from "../components/fields/FieldsRow";
import DetailPage from "../components/pages/DetailPage";
import {
  isCharacterCategory,
  SceneSheets,
  SheetCategory,
} from "../common/sheet";
import { DocumentSnapshot } from "@atgof-firebase/firebase";
import { LanguageContext } from "../common/language";
import { RootStackParamList } from "../types";
import { SheetSections } from "../components/SheetSections";
import { episodeLabel } from "../common/util";
import PhotoStrip from "../components/PhotoStrip";
import { referencePathForApplicand } from "../data/backend/downloader";
import { useDocument } from "../data/useDocument";
import { useDocumentReference } from "../data/firestore";
import { useProject } from "../data/useProject";
import _ from "lodash";
import { useEditor } from "../components/pages/NavPage";
import { ProjectContext } from "../data/projectContext";
import { useSnapshot } from "../data/useSnapshot";
import {
  kindOfApplicand,
  onApplicandSceneSheets,
  subjectSpecForApplicand,
  UserFacingApplicandKind,
  userFacingApplicandKind,
} from "@atgof-common/applicand";
import SectionHeader from "../components/SectionHeader";
import { BreakdownSheetsForScenes } from "../components/BreakdownSheets";
import { useUserInkColours } from "../data/useUserInkColours";

export function applicandTitle(
  ph: (k: PhraseKey) => Phrase,
  applicand: DocumentSnapshot | undefined,
  subjectIsCharacter: boolean,
  isNew: boolean = false,
) {
  const applicandKind = kindOfApplicand(applicand?.ref);
  const userFacingKind: UserFacingApplicandKind = applicandKind
    ? userFacingApplicandKind(applicandKind, subjectIsCharacter)
    : "item";
  return (
    applicand?.get("displayName") ||
    ph((isNew ? "new-" : "unnamed-") + userFacingKind)
  );
}

export default function ApplicandScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "applicand">) {
  const { ph } = React.useContext(LanguageContext);
  const { adminCategories } = React.useContext(ProjectContext);
  const { path, episodePath, subjectName } = route.params;
  const isNew = route.params.isNew === "true";
  const applicandRef = useDocumentReference(path);
  const applicandKind = kindOfApplicand(applicandRef);
  const applicand = useDocument(applicandRef);
  const category: SheetCategory | undefined = applicand?.get("category");
  const userFacingKind = applicandKind
    ? userFacingApplicandKind(applicandKind, isCharacterCategory(category))
    : undefined;
  const project = useDocument(useProject());
  const extraPhotoStrips: string[] | undefined =
    category && applicandKind
      ? Object.keys(
          _.get(
            project?.get("breakdownSettings"),
            [applicandKind, category, "photoStrips"],
            {},
          ),
        )
      : undefined;
  const referencePath = referencePathForApplicand(applicand?.ref);
  const sceneSheets = useSnapshot<SceneSheets[] | undefined>(
    _.curry(onApplicandSceneSheets)(applicand),
    [applicand],
  );
  const canEditApplicand = category && adminCategories.includes(category);
  const { editor, editingMode } = useEditor(true, "done");
  const inkColours = useUserInkColours();
  const readonly = !(isNew || episodePath || editingMode);
  return (
    <DetailPage
      title={`${subjectName} - ${episodePath ? episodeLabel(ph, episodePath, true) : applicandTitle(ph, applicand, isNew)}`}
      deleteItem={applicand?.ref}
      editor={canEditApplicand && !isNew ? editor : undefined}
    >
      {applicand && category && userFacingKind && (
        <View>
          <FieldsRow
            doc={applicand}
            readonly={readonly}
            fields={[
              {
                key: "displayName",
                labelKey: `${userFacingKind}-name`,
                kind: "text",
                isTitle: true,
              },
              {
                key: "description",
                labelKey: `${userFacingKind}-description`,
                kind: "text",
              },
            ]}
          />
          <PhotoStrip
            sheetRef={applicand.ref}
            sheetCategory={category}
            referencePath={referencePath}
            style={{ marginTop: 16, marginBottom: 16 }}
            readonly={readonly}
          />
          <SheetSections
            doc={applicand}
            category={category}
            docIsApplicand
            readonly={readonly}
          />
          <View>
            {extraPhotoStrips?.map((kind) => (
              <PhotoStrip
                key={kind}
                sheetRef={applicand.ref}
                sheetCategory={category}
                referencePath={referencePath}
                style={{ marginTop: 16, marginBottom: 16 }}
                kind={kind}
                readonly={readonly}
              />
            ))}
          </View>
          {!(isNew || episodePath) && (
            <>
              <SectionHeader title={ph("scenes") as string} />
              <BreakdownSheetsForScenes
                sceneSheets={sceneSheets}
                subject={subjectSpecForApplicand(applicand.ref)!}
                episodePath={path}
                sheetCategories={[applicand.get("category")]}
                inkColours={inkColours}
              />
            </>
          )}
        </View>
      )}
    </DetailPage>
  );
}
